import React, { useEffect, useState } from "react";
import "../../assets/css/tutor-details.css";
import { fetchData, getData, updateData } from "../firebase/firebase_methods";
import { databaseKey } from "../common/database_key";
import { handleError } from "../common/error_method";
import {
  cleanData,
  fetchLanguages,
  fetchSubjects,
  saveNewItem,
} from "./tutor_methods";
import MultiSelectField from "./multiselect-fields";
import TimeSlotSelector from "./time-slots";
import DynamicField from "./dynamic-field";
import { City } from "country-state-city";
import { toast } from "react-toastify";

const TutorProfile = ({ isTutor, userId }) => {
  const [tutorDetails, setTutorDetails] = useState({
    subjects: [],
    experience: "",
    qualification: "",
    timings: [],
    pricing: { type: "hourly", amount: "" },
    mode: "online",
    bio: "",
    languages: [],
    teachingLocation: { type: "home", address: "" },
    cities: [],
    onlinePlatform: "",
    dynamicFields: [],
  });

  const [isSaving, setIsSaving] = useState(false);
  const [allSubjects, setAllSubjects] = useState([]);
  const [allLanguages, setAllLanguages] = useState([]);
  const [allCities, setAllCities] = useState([]);

  const collectionName = databaseKey();

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const userRef = fetchData(`${collectionName}/tutors/${userId}`);
        const userSnapshot = await getData(userRef);

        const subjects = await fetchSubjects();
        setAllSubjects(
          subjects.map((sub) => ({ value: sub.name, label: sub.name }))
        );

        const languages = await fetchLanguages();
        setAllLanguages(
          languages.map((lang) => ({ value: lang.name, label: lang.name }))
        );

        const cities = City.getCitiesOfCountry("IN");
        setAllCities(
          cities.map((city) => ({ value: city.name, label: city.name }))
        );

        if (userSnapshot.exists()) {
          const specificUserData = userSnapshot.val();
          setTutorDetails((prevDetails) => ({
            ...prevDetails,
            ...specificUserData,
            subjects: specificUserData.subjects || [],
            languages: specificUserData.languages || [],
            cities: specificUserData.cities || [],
            teachingLocation: specificUserData.teachingLocation || {
              type: "home",
              address: "",
            },
            pricing: specificUserData.pricing || { type: "hourly", amount: "" },
            timings: specificUserData.timings || [],
            dynamicFields: specificUserData.dynamicFields || [],
          }));
        }
      } catch (error) {
        handleError("Error fetching user details: " + error);
      }
    };

    if (userId) fetchUserDetails();
  }, [isTutor, userId, collectionName]);

  const handleChange = ({ target: { name, value } }) => {
    setTutorDetails((prevDetails) => ({
      ...prevDetails,
      [name]: typeof prevDetails[name] === "number" ? Number(value) : value,
    }));
  };

  const handleMultiSelectChange = async (field, selectedOptions) => {
    const values = selectedOptions.map((option) => option.value);

    const newItems = selectedOptions.filter(
      (option) =>
        ![...allSubjects, ...allLanguages, ...allCities].some(
          (item) => item.value === option.value
        )
    );

    for (const newItem of newItems) {
      await saveNewItem(field, newItem.value);
      if (field === "subjects") {
        setAllSubjects((prev) => [
          ...prev,
          { value: newItem.value, label: newItem.value },
        ]);
      } else if (field === "languages") {
        setAllLanguages((prev) => [
          ...prev,
          { value: newItem.value, label: newItem.value },
        ]);
      }
    }

    setTutorDetails((prevDetails) => ({
      ...prevDetails,
      [field]: values,
    }));
  };

  const handlePricingChange = (field, value) => {
    setTutorDetails((prevDetails) => ({
      ...prevDetails,
      pricing: {
        ...prevDetails.pricing,
        [field]: value,
      },
    }));
  };

  const handleTeachingLocationChange = (field, value) => {
    setTutorDetails((prevDetails) => ({
      ...prevDetails,
      teachingLocation: {
        ...prevDetails.teachingLocation,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tutorDetails.bio.trim()) {
      toast.error("Bio is required.");
      return;
    }

    if (tutorDetails.subjects.length === 0) {
      toast.error("Please add at least one subject.");
      return;
    }

    try {
      const profileRef = fetchData(`${collectionName}/tutors/${userId}`);
      const cleanedData = cleanData(tutorDetails);

      await updateData(profileRef, {
        ...cleanedData,
        timestamp: new Date().toISOString(),
      });

      toast.success("Tutor details saved successfully! ");
    } catch (error) {
      handleError("Error saving tutor details: " + error);
      toast.error(" Error saving tutor details. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="tutor-profile-container">
      {isTutor && (
        <div className="tutor-form">
          <h2 className="form-title">Tutor Profile</h2>

          <div className="form-section">
            <textarea
              name="bio"
              placeholder="Brief Bio/Introduction *"
              value={tutorDetails.bio}
              onChange={handleChange}
              required
            />
          </div>

          <MultiSelectField
            label="Subjects"
            field="subjects"
            options={allSubjects}
            selectedValues={tutorDetails.subjects}
            onChange={handleMultiSelectChange}
          />

          <MultiSelectField
            label="Languages"
            field="languages"
            options={allLanguages}
            selectedValues={tutorDetails.languages}
            onChange={handleMultiSelectChange}
          />

          <MultiSelectField
            label="Cities"
            field="cities"
            options={allCities}
            selectedValues={tutorDetails.cities}
            onChange={handleMultiSelectChange}
          />

          <div className="form-section">
            <div className="row">
              <div className="col-md-4 form-group mb-3 mb-md-0">
                <input
                  type="number"
                  name="experience"
                  placeholder="Experience (Years) (optional)"
                  value={tutorDetails.experience || ""}
                  onChange={(e) => {
                    const value = Math.max(0, e.target.value);
                    handleChange({ target: { name: "experience", value } });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e") {
                      e.preventDefault();
                    }
                  }}
                  className="form-control"
                  min="0"
                />
              </div>

              <div className="col-md-8 form-group mb-3 mb-md-0">
                <input
                  type="text"
                  name="qualification"
                  placeholder="Qualification (optional)"
                  value={tutorDetails.qualification}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="row">
              <div className="col-md-4 form-group mb-3 mb-md-0">
                <select
                  value={tutorDetails.pricing.type}
                  onChange={(e) => handlePricingChange("type", e.target.value)}
                  className="form-control"
                >
                  <option value="hourly">Hourly Fee</option>
                  <option value="weekly">Weekly Fee</option>
                  <option value="monthly">Monthly Fee</option>
                </select>
              </div>

              <div className="col-md-8 form-group mb-3 mb-md-0">
                <input
                  type="number"
                  placeholder="Amount"
                  value={tutorDetails.pricing.amount || ""}
                  onChange={(e) => {
                    const value = Math.max(0, Number(e.target.value));
                    handlePricingChange("amount", value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e") {
                      e.preventDefault();
                    }
                  }}
                  className="form-control"
                  min="0"
                />
              </div>
            </div>
          </div>

          <div className="row align-items-center">
            <div className="col-md-4 form-group">
              <select
                name="mode"
                value={tutorDetails.mode}
                onChange={handleChange}
                className="form-control"
              >
                <option value="online">Online</option>
                <option value="offline">Offline</option>
              </select>
            </div>

            {tutorDetails.mode === "offline" && (
              <>
                <div className="col-md-4 form-group">
                  <select
                    value={tutorDetails.teachingLocation.type}
                    onChange={(e) =>
                      handleTeachingLocationChange("type", e.target.value)
                    }
                    className="form-control"
                  >
                    <option value="home">Home Tutoring</option>
                    <option value="center">Tutoring Center</option>
                  </select>
                </div>

                <div className="col-md-4 form-group">
                  <input
                    type="text"
                    placeholder="Enter Address"
                    value={tutorDetails.teachingLocation.address}
                    onChange={(e) =>
                      handleTeachingLocationChange("address", e.target.value)
                    }
                    className="form-control"
                  />
                </div>
              </>
            )}

            {tutorDetails.mode === "online" && (
              <div className="col-md-3 form-group">
                <input
                  type="text"
                  name="onlinePlatform"
                  placeholder="Zoom, Google Meet, etc."
                  value={tutorDetails.onlinePlatform}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>
            )}
          </div>

          <TimeSlotSelector
            timings={tutorDetails.timings}
            setTutorDetails={setTutorDetails}
            tutorDetails={tutorDetails}
          />

          <DynamicField
            tutorDetails={tutorDetails}
            setTutorDetails={setTutorDetails}
          />

          <button
            type="submit"
            className="save-button"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Tutor Details"}
          </button>
        </div>
      )}
    </div>
  );
};

export default TutorProfile;
